import React from 'react';
import {
  IMAGE_MODEL_OPTIONS,
  filterModelOptionsByUsage,
  getStaticModelOption,
  ImageModelSelector,
  MODEL_OPTIONS,
  ModelSelector,
  normalizeModelId,
  sortModelOptionsByCatalog,
  type ImageModelOption,
  type ImageModelProvider,
  type ImageModelSelectorProps,
  type ModelOption,
  type ModelSelectorProps,
  type ModelSelectorProvider,
} from '@modules/ui-components';
import { useAvailableAIModels } from '../hooks/useAvailableAIModels';
import type { AIProviderType, AIProvidersStatus } from '../types/aiKey.types';
import type {
  AvailableAIImageModel,
  AvailableAITextModel,
  ProviderModelAvailability,
} from '../types/aiModel.types';

const TEXT_SELECTOR_PROVIDERS: ModelSelectorProvider[] = [
  'anthropic',
  'openai',
  'google',
  'perplexity',
];
const IMAGE_SELECTOR_PROVIDERS: ImageModelProvider[] = ['openai', 'google'];

function isTextSelectorProvider(provider: AIProviderType): provider is ModelSelectorProvider {
  return TEXT_SELECTOR_PROVIDERS.includes(provider as ModelSelectorProvider);
}

function isImageSelectorProvider(provider: AIProviderType): provider is ImageModelProvider {
  return IMAGE_SELECTOR_PROVIDERS.includes(provider as ImageModelProvider);
}

function toModelOption(model: AvailableAITextModel): ModelOption | null {
  if (!isTextSelectorProvider(model.provider)) return null;
  const modelId = normalizeModelId(model.id);
  const fallback = getStaticModelOption(modelId);
  return {
    value: modelId,
    label: fallback?.label ?? model.displayName ?? model.id,
    provider: model.provider,
    deepResearchOnly: fallback?.deepResearchOnly,
    deepResearchCapable: fallback?.deepResearchCapable,
    visionCapable: model.supportsVision ?? fallback?.visionCapable,
  };
}

function toImageModelOption(model: AvailableAIImageModel): ImageModelOption | null {
  if (!isImageSelectorProvider(model.provider)) return null;
  const fallback = IMAGE_MODEL_OPTIONS.find((option) => option.value === model.id);
  return {
    value: model.id,
    label: fallback?.label ?? model.displayName ?? model.id,
    provider: model.provider,
  };
}

function mergeTextOptionsWithStaticCatalog(
  options: ModelOption[],
  allowedProviders: ReadonlySet<ModelSelectorProvider> | null,
): ReadonlyArray<ModelOption> {
  if (!allowedProviders) return MODEL_OPTIONS;
  if (allowedProviders.size === 0) return [];

  const merged = new Map<string, ModelOption>();
  for (const option of options) {
    if (!allowedProviders.has(option.provider)) continue;
    merged.set(normalizeModelId(option.value), option);
  }
  for (const option of MODEL_OPTIONS) {
    if (!allowedProviders.has(option.provider)) continue;
    const key = normalizeModelId(option.value);
    if (!merged.has(key)) merged.set(key, option);
  }
  return sortModelOptionsByCatalog(Array.from(merged.values()));
}

function mergeImageOptionsWithStaticCatalog(
  options: ImageModelOption[],
  allowedProviders: ReadonlySet<ImageModelProvider> | null,
): ReadonlyArray<ImageModelOption> {
  if (!allowedProviders) return IMAGE_MODEL_OPTIONS;
  if (allowedProviders.size === 0) return [];

  const merged = new Map<string, ImageModelOption>();
  for (const option of options) {
    if (!allowedProviders.has(option.provider)) continue;
    merged.set(option.value, option);
  }
  for (const option of IMAGE_MODEL_OPTIONS) {
    if (!allowedProviders.has(option.provider)) continue;
    if (!merged.has(option.value)) merged.set(option.value, option);
  }
  return Array.from(merged.values());
}

function getProjectConfiguredProviderSet<TProvider extends AIProviderType>(
  projectProviders: AIProvidersStatus,
  candidates: ReadonlyArray<TProvider>,
): Set<TProvider> | null {
  const configured = candidates.filter((provider) => projectProviders[provider]?.configured);
  return configured.length > 0 ? new Set(configured) : null;
}

function getResolvedProviderSet<TProvider extends AIProviderType>(
  modelProviders: Partial<Record<AIProviderType, ProviderModelAvailability>>,
  candidates: ReadonlyArray<TProvider>,
  capability: 'textModels' | 'imageModels',
): Set<TProvider> | null {
  const available = candidates
    .map((provider) => ({ provider, availability: modelProviders[provider] }))
    .filter(({ availability }) => Boolean(availability?.configured && availability[capability]));

  if (available.length === 0) return null;

  const preferredSource = (['user', 'admin', 'platform'] as const)
    .find((source) => available.some(({ availability }) => availability?.source === source));

  const filtered = preferredSource
    ? available.filter(({ availability }) => availability?.source === preferredSource)
    : available;

  return new Set(filtered.map(({ provider }) => provider));
}

interface AvailabilitySelectorProps {
  /**
   * Project whose resolved AI keys define the visible model catalog.
   * When omitted, the underlying selector falls back to its static catalog.
   */
  projectId?: string;
}

export interface AvailableModelSelectorProps
  extends AvailabilitySelectorProps,
    Omit<ModelSelectorProps, 'options'> {}

export interface AvailableImageModelSelectorProps
  extends AvailabilitySelectorProps,
    Omit<ImageModelSelectorProps, 'options'> {}

export const AvailableModelSelector: React.FC<AvailableModelSelectorProps> = ({
  projectId,
  value,
  onChange,
  providers,
  disabled,
  modelUsage,
  ...props
}) => {
  const {
    textModels,
    providers: modelProviders,
    projectProviders,
    loading,
    reload,
  } = useAvailableAIModels(projectId);
  const usesAvailability = Boolean(projectId);
  const providerCandidates = providers ?? TEXT_SELECTOR_PROVIDERS;
  const allowedProviders = React.useMemo(
    () =>
      usesAvailability
        ? getProjectConfiguredProviderSet(projectProviders, providerCandidates) ??
          getResolvedProviderSet(modelProviders, providerCandidates, 'textModels') ??
          new Set<ModelSelectorProvider>()
        : null,
    [modelProviders, projectProviders, providerCandidates, usesAvailability]
  );
  const options = React.useMemo(
    () => textModels.map(toModelOption).filter(Boolean) as ModelOption[],
    [textModels]
  );
  const selectorOptions = React.useMemo(
    () => mergeTextOptionsWithStaticCatalog(options, allowedProviders),
    [allowedProviders, options]
  );
  const visibleOptions = React.useMemo(
    () =>
      filterModelOptionsByUsage(
        providers
          ? selectorOptions.filter((option) => providers.includes(option.provider))
          : selectorOptions,
        modelUsage,
      ),
    [modelUsage, providers, selectorOptions]
  );
  const handleOpen = React.useCallback(() => {
    if (projectId) void reload();
  }, [projectId, reload]);

  React.useEffect(() => {
    if (!projectId || loading || visibleOptions.length === 0) return;
    const selectedValue = normalizeModelId(value);
    if (visibleOptions.some((option) => option.value === selectedValue)) return;
    onChange(visibleOptions[0].value);
  }, [loading, projectId, onChange, value, visibleOptions]);

  const hasVisibleOptions = visibleOptions.length > 0;

  return (
    <ModelSelector
      {...props}
      value={value}
      onChange={onChange}
      providers={providers}
      disabled={disabled || (!loading && !hasVisibleOptions)}
      loading={usesAvailability && loading}
      modelUsage={modelUsage}
      onOpen={handleOpen}
      options={usesAvailability ? selectorOptions : undefined}
    />
  );
};

export const AvailableImageModelSelector: React.FC<AvailableImageModelSelectorProps> = ({
  projectId,
  value,
  onChange,
  providers,
  disabled,
  ...props
}) => {
  const {
    imageModels,
    providers: modelProviders,
    projectProviders,
    loading,
    reload,
  } = useAvailableAIModels(projectId);
  const usesAvailability = Boolean(projectId);
  const providerCandidates = providers ?? IMAGE_SELECTOR_PROVIDERS;
  const allowedProviders = React.useMemo(
    () =>
      usesAvailability
        ? getProjectConfiguredProviderSet(projectProviders, providerCandidates) ??
          getResolvedProviderSet(modelProviders, providerCandidates, 'imageModels') ??
          new Set<ImageModelProvider>()
        : null,
    [modelProviders, projectProviders, providerCandidates, usesAvailability]
  );
  const options = React.useMemo(
    () => imageModels.map(toImageModelOption).filter(Boolean) as ImageModelOption[],
    [imageModels]
  );
  const selectorOptions = React.useMemo(
    () => mergeImageOptionsWithStaticCatalog(options, allowedProviders),
    [allowedProviders, options]
  );
  const visibleOptions = React.useMemo(
    () =>
      providers
        ? selectorOptions.filter((option) => providers.includes(option.provider))
        : selectorOptions,
    [providers, selectorOptions]
  );
  const handleOpen = React.useCallback(() => {
    if (projectId) void reload();
  }, [projectId, reload]);

  React.useEffect(() => {
    if (!projectId || loading || visibleOptions.length === 0) return;
    if (visibleOptions.some((option) => option.value === value)) return;
    onChange(visibleOptions[0].value);
  }, [loading, projectId, onChange, value, visibleOptions]);

  const hasVisibleOptions = visibleOptions.length > 0;

  return (
    <ImageModelSelector
      {...props}
      value={value}
      onChange={onChange}
      providers={providers}
      disabled={disabled || (!loading && !hasVisibleOptions)}
      loading={usesAvailability && loading}
      onOpen={handleOpen}
      options={usesAvailability ? selectorOptions : undefined}
    />
  );
};
